import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  useCreateEmployeeESignature,
  useDeleteEmployeeESignature,
  useEmployeeESignatures,
  useUpdateEmployeeESignature,
} from "../api/employeeDocs";
import {
  ESIGNATURE_MEDIA,
  ESIGNATURE_PROVIDERS,
  ESIGNATURE_TYPES,
  type EmployeeESignature,
  type ESignatureMedium,
  type ESignatureProvider,
  type ESignatureType,
  type ExaminationStatus,
} from "../types.employee_docs";

interface Props {
  userId: string;
}

const STATUS_BADGE: Record<ExaminationStatus, { color: "green" | "yellow" | "red" | "gray"; label: string }> = {
  ok:      { color: "green",  label: "Ważny" },
  soon:    { color: "yellow", label: "Wkrótce wygasa" },
  expired: { color: "red",    label: "Wygasły" },
  missing: { color: "gray",   label: "Brak daty" },
};

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL");
}

interface FormState {
  provider: ESignatureProvider | "";
  signature_type: ESignatureType;
  medium: ESignatureMedium;
  serial_number: string;
  issued_at: string;
  valid_until: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  provider: "",
  signature_type: "qualified",
  medium: "",
  serial_number: "",
  issued_at: "",
  valid_until: "",
  notes: "",
};

function toFormState(sig: EmployeeESignature): FormState {
  return {
    provider: sig.provider,
    signature_type: sig.signature_type,
    medium: sig.medium,
    serial_number: sig.serial_number,
    issued_at: sig.issued_at ?? "",
    valid_until: sig.valid_until ?? "",
    notes: sig.notes,
  };
}

export function EmployeeESignaturePanel({ userId }: Props) {
  const { data: signatures = [], isLoading } = useEmployeeESignatures(userId);
  const createMut = useCreateEmployeeESignature(userId);
  const updateMut = useUpdateEmployeeESignature(userId);
  const deleteMut = useDeleteEmployeeESignature(userId);

  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  function startNew() {
    setForm(EMPTY_FORM);
    setError(null);
    setEditingId("new");
  }

  function startEdit(sig: EmployeeESignature) {
    setForm(toFormState(sig));
    setError(null);
    setEditingId(sig.id);
  }

  function cancel() {
    setEditingId(null);
    setError(null);
  }

  function buildPayload() {
    return {
      provider: form.provider as ESignatureProvider,
      signature_type: form.signature_type,
      medium: form.medium as ESignatureMedium,
      serial_number: form.serial_number,
      issued_at:   form.issued_at   || null,
      valid_until: form.valid_until || null,
      notes: form.notes,
    };
  }

  function handleSave() {
    if (!form.provider) {
      setError("Wybierz dostawcę.");
      return;
    }
    if (form.issued_at && form.valid_until && form.valid_until < form.issued_at) {
      setError("Data ważności nie może być wcześniejsza niż data wydania.");
      return;
    }
    setError(null);
    const payload = buildPayload();
    if (editingId === "new") {
      createMut.mutate(payload, {
        onSuccess: () => cancel(),
        onError:   () => setError("Błąd zapisu."),
      });
    } else if (editingId) {
      updateMut.mutate({ id: editingId, ...payload }, {
        onSuccess: () => cancel(),
        onError:   () => setError("Błąd zapisu."),
      });
    }
  }

  function handleDelete(sig: EmployeeESignature) {
    if (!confirm(`Usunąć podpis ${sig.provider_display}?`)) return;
    deleteMut.mutate(sig.id);
  }

  if (isLoading) {
    return <div className="text-sm text-[var(--color-text-tertiary)] py-2">Ładowanie podpisów…</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
          Podpisy elektroniczne
        </h4>
        {editingId === null && (
          <Button size="sm" onClick={startNew}>
            + Dodaj podpis
          </Button>
        )}
      </div>

      {signatures.length === 0 && editingId === null && (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Brak zarejestrowanych podpisów elektronicznych.
        </p>
      )}

      {signatures.map((sig) => {
        const badge = STATUS_BADGE[sig.status.status];
        const isEditing = editingId === sig.id;
        if (isEditing) {
          return (
            <ESignatureForm
              key={sig.id}
              form={form}
              setForm={setForm}
              error={error}
              saving={updateMut.isPending}
              onSave={handleSave}
              onCancel={cancel}
            />
          );
        }
        return (
          <div
            key={sig.id}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 space-y-1.5"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-[var(--color-text-primary)]">
                  {sig.provider_display}
                </span>
                <Badge color="gray">{sig.signature_type_display}</Badge>
                {sig.medium_display && <Badge color="blue">{sig.medium_display}</Badge>}
                <Badge color={badge.color}>{badge.label}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="text-xs text-[var(--color-primary)] hover:underline"
                  onClick={() => startEdit(sig)}
                >
                  Edytuj
                </button>
                <button
                  className="text-xs text-red-500 hover:underline"
                  onClick={() => handleDelete(sig)}
                  disabled={deleteMut.isPending}
                >
                  Usuń
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-[var(--color-text-secondary)]">
              <span>
                Nr certyfikatu:{" "}
                <span className="font-medium text-[var(--color-text-primary)]">
                  {sig.serial_number || "—"}
                </span>
              </span>
              <span>
                Wydany:{" "}
                <span className="font-medium text-[var(--color-text-primary)]">
                  {formatDate(sig.issued_at)}
                </span>
              </span>
              <span>
                Ważny do:{" "}
                <span className="font-medium text-[var(--color-text-primary)]">
                  {formatDate(sig.valid_until)}
                </span>
                {sig.status.days_remaining !== null && sig.status.status !== "missing" && (
                  <span className="text-[10px] text-[var(--color-text-tertiary)] ml-1">
                    ({sig.status.days_remaining >= 0
                      ? `${sig.status.days_remaining} dni`
                      : `minęło ${Math.abs(sig.status.days_remaining)} dni`})
                  </span>
                )}
              </span>
            </div>
            {sig.notes && (
              <p className="text-xs text-[var(--color-text-tertiary)] italic">{sig.notes}</p>
            )}
          </div>
        );
      })}

      {editingId === "new" && (
        <ESignatureForm
          form={form}
          setForm={setForm}
          error={error}
          saving={createMut.isPending}
          onSave={handleSave}
          onCancel={cancel}
        />
      )}
    </div>
  );
}

interface FormProps {
  form: FormState;
  setForm: (updater: (prev: FormState) => FormState) => void;
  error: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

function ESignatureForm({ form, setForm, error, saving, onSave, onCancel }: FormProps) {
  return (
    <div className="rounded-lg border border-[var(--color-primary)] bg-[var(--color-bg-elevated)] p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Dostawca *</span>
          <select
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
            value={form.provider}
            onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value as ESignatureProvider | "" }))}
          >
            <option value="">— wybierz —</option>
            {ESIGNATURE_PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Typ podpisu</span>
          <select
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
            value={form.signature_type}
            onChange={(e) => setForm((f) => ({ ...f, signature_type: e.target.value as ESignatureType }))}
          >
            {ESIGNATURE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Nośnik</span>
          <select
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
            value={form.medium}
            onChange={(e) => setForm((f) => ({ ...f, medium: e.target.value as ESignatureMedium }))}
          >
            <option value="">— nie podano —</option>
            {ESIGNATURE_MEDIA.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Nr certyfikatu / seryjny</span>
          <input
            type="text"
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={form.serial_number}
            onChange={(e) => setForm((f) => ({ ...f, serial_number: e.target.value }))}
            placeholder="np. 6F3A2B1C..."
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Data wydania</span>
          <DatePicker
            value={form.issued_at}
            onChange={(v) => setForm((f) => ({ ...f, issued_at: v }))}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">Ważny do</span>
          <DatePicker
            value={form.valid_until}
            onChange={(v) => setForm((f) => ({ ...f, valid_until: v }))}
          />
        </label>
      </div>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-[var(--color-text-secondary)]">Uwagi</span>
        <textarea
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-none"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </label>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={saving}>
          {saving ? "Zapisywanie…" : "Zapisz"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}
